<template>
    <v-dialog v-model="internalDialog" max-width="600" persistent>
        <v-card>
            <v-card-title class="text-h5">
                Add New Company
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <!-- Company Name -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Company Name <span class="red--text">*</span>
                        </label>
                        <v-text-field v-model="formData.name" placeholder="Enter company name" outlined dense
                            :rules="[rules.required]"></v-text-field>
                    </div>

                    <!-- Description -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Description
                        </label>
                        <v-textarea v-model="formData.description"
                            placeholder="Brief description of the company or project" outlined dense rows="3"
                            auto-grow></v-textarea>
                    </div>

                    <!-- Status -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Status
                        </label>
                        <v-select v-model="formData.status" :items="statusOptions" placeholder="Active" outlined
                            dense></v-select>
                    </div>

                    <!-- Due Date -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Due Date
                        </label>
                        <v-menu
                            v-model="dueDateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="formData.due_date"
                                    placeholder="Select due date"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                    clearable
                                    @click:clear="formData.due_date = null"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="formData.due_date"
                                @input="dueDateMenu = false"
                                no-title
                                scrollable
                            ></v-date-picker>
                        </v-menu>
                    </div>

                    <!-- Team Members -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Team Members
                        </label>
                        <v-autocomplete v-model="formData.team_members" :items="usersList" item-text="name" item-value="id"
                            placeholder="Add team member" outlined dense multiple chips small-chips deletable-chips>
                            <template v-slot:selection="{ item, index }">
                                <v-chip v-if="index < 3" small close @click:close="removeTeamMember(item.id)">
                                    {{ item.name }}
                                </v-chip>
                                <span v-if="index === 3" class="grey--text text-caption">
                                    (+{{ formData.team_members.length - 3 }} others)
                                </span>
                            </template>
                        </v-autocomplete>
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="createCompany" :loading="loading" :disabled="!valid">
                    Create Company
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'CreateCompanyDialog',
    inject: ['users'],
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            internalDialog: this.value,
            valid: false,
            loading: false,
            dueDateMenu: false,
            formData: {
                name: '',
                description: '',
                status: 'Active',
                due_date: null,
                team_members: []
            },
            statusOptions: ['Active', 'On-Hold', 'Closed'],
            rules: {
                required: (v) => !!v || 'This field is required'
            }
        };
    },
    computed: {
        usersList() {
            return this.users() || [];
        }
    },
    watch: {
        value(val) {
            this.internalDialog = val;
        },
        internalDialog(val) {
            this.$emit('input', val);
            if (!val) {
                this.resetForm();
            }
        }
    },
    methods: {
        closeDialog() {
            this.internalDialog = false;
        },
        removeTeamMember(memberId) {
            const index = this.formData.team_members.indexOf(memberId);
            if (index > -1) {
                this.formData.team_members.splice(index, 1);
            }
        },
        resetForm() {
            this.formData = {
                name: '',
                description: '',
                status: 'Active',
                due_date: null,
                team_members: []
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        async createCompany() {
            if (!this.$refs.form.validate()) {
                return;
            }

            this.loading = true;
            try {
                // API call will be implemented by backend
                const response = await this.$axios.post('api/companies/create', this.formData);

                if (response.data.response) {
                    this.$emit('company-created', response.data.company);
                    this.$toast.success('Company created successfully');
                    this.closeDialog();
                } else {
                    this.$toast.error(response.data.message || 'Failed to create company');
                }
            } catch (error) {
                console.error('Error creating company:', error);
                this.$toast.error('An error occurred while creating the company');
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.v-card-title {
    background-color: #f5f5f5;
}

label {
    color: #333;
}
</style>
